/**
 * DeferredPostHogProvider - Lazy-loads PostHog after first paint for better initial load performance
 *
 * This component defers PostHog initialization until after the browser's first idle period,
 * significantly reducing Total Blocking Time (TBT) and Time to Interactive (TTI).
 *
 * CRITICAL: We do NOT statically import @posthog/react or posthog-js here. Both packages
 * are dynamically imported only when needed to avoid adding them to the initial bundle.
 *
 * Children using usePostHog() from PostHogContext will receive undefined until PostHog is ready,
 * which is already handled by safeCapture/captureException in capture.ts.
 */
import * as React from 'react'
import { PostHogContextProvider } from './PostHogContext'
import { getPostHogConfig, shouldInitializePostHog } from './config'
import type { PostHog } from 'posthog-js'

interface DeferredPostHogProviderProps {
  children: React.ReactNode
  apiKey: string
  options?: Parameters<typeof getPostHogConfig>[0]
}

// Delay before loading PostHog (ms) - gives critical content time to render first
const POSTHOG_LOAD_DELAY_MS = 2000

// Fallback for browsers without requestIdleCallback (Safari)
// Uses a reasonable delay to avoid blocking initial render
const requestIdleCallbackPolyfill =
  typeof window !== 'undefined' && 'requestIdleCallback' in window
    ? window.requestIdleCallback
    : (cb: () => void) => setTimeout(cb, POSTHOG_LOAD_DELAY_MS)

/**
 * DeferredPostHogProvider wraps children with our custom PostHogContextProvider after:
 * 1. Component has mounted (client-side)
 * 2. Browser is idle (via requestIdleCallback)
 * 3. PostHog SDK has been dynamically imported and initialized
 *
 * Until then, children render with PostHog context returning undefined.
 *
 * NOTE: We use our own PostHogContextProvider instead of @posthog/react's PostHogProvider
 * to avoid pulling the @posthog/react package into the initial bundle. Consumers use
 * usePostHog() from PostHogContext.tsx instead of from @posthog/react.
 */
export function DeferredPostHogProvider({
  children,
  apiKey,
  options,
}: DeferredPostHogProviderProps) {
  const [posthogInstance, setPosthogInstance] = React.useState<PostHog | undefined>(undefined)

  React.useEffect(() => {
    // Only run on client
    if (typeof window === 'undefined') return

    const mode = import.meta.env.MODE || 'development'
    const devEnabled = import.meta.env.VITE_POSTHOG_DEV_ENABLED

    // Don't load if PostHog shouldn't be initialized
    if (!shouldInitializePostHog(mode, devEnabled) || !apiKey) {
      return
    }

    // AbortController provides a cleaner pattern for cancellation that TypeScript understands
    const abortController = new AbortController()
    const isCancelled = () => abortController.signal.aborted

    // Defer loading until browser is idle to avoid blocking initial paint
    const idleCallbackId = requestIdleCallbackPolyfill(async () => {
      if (isCancelled()) return

      try {
        // Dynamic import posthog-js only - no @posthog/react needed anymore!
        // This keeps the bundle significantly smaller
        const posthogModule = await import('posthog-js')
        const posthog = posthogModule.default

        if (isCancelled()) return

        // Initialize with config
        const config = options
          ? getPostHogConfig(options, false)
          : getPostHogConfig(
              {
                VITE_PUBLIC_POSTHOG_KEY: apiKey,
                VITE_PUBLIC_POSTHOG_HOST:
                  import.meta.env.VITE_PUBLIC_POSTHOG_HOST || '',
                MODE: mode,
              },
              false,
            )

        posthog.init(apiKey, {
          ...config,
          // Ensure we don't double-initialize
          loaded: (ph) => {
            // Call the original loaded callback from config
            // config.loaded is always defined from getPostHogConfig
            config.loaded(ph)
            // Only log in development
            if (mode === 'development') {
              console.log('PostHog loaded (deferred)')
            }
          },
        })

        // Register app version so PostHog can match errors to source maps
        const appVersion = import.meta.env.VITE_PUBLIC_POSTHOG_VERSION
        if (appVersion) {
          posthog.register({ $version: appVersion })
        }

        if (isCancelled()) return

        setPosthogInstance(posthog)
      } catch (error) {
        console.error('Failed to load PostHog:', error)
        // Don't block the app - just render children without PostHog
      }
    })

    return () => {
      abortController.abort()
      if (
        typeof window !== 'undefined' &&
        'cancelIdleCallback' in window &&
        typeof idleCallbackId === 'number'
      ) {
        window.cancelIdleCallback(idleCallbackId)
      }
    }
  }, [apiKey, options])

  // Always wrap children with context provider
  // posthogInstance will be undefined until loaded, which consumers handle
  return (
    <PostHogContextProvider client={posthogInstance}>
      {children}
    </PostHogContextProvider>
  )
}
